import { useCallback, useRef } from "react";
import { useDispatch } from "react-redux";

import {
  EMPTY_HOVER_LEGEND,
  useHoverHighlightClass,
  useHoverLegendWriter
} from "atoms/interaction";
import { useChartsContext } from "contexts";
import { subscriptProductTokens } from "entities/charts/eva/translators/utils";
import { setActiveFocus } from "store/features";
import styled from "styled-components";
import { removeProbitInfoFromtitle } from "utils/removeProbitInfoFromTitle";

import { useChartEntities } from "hooks/charts";

import { EntityKind } from "models/entityKind";

import { LineStylePreview } from "components/charts/components/ProductSelector/components/LineStylePreview";
import { removeRescatFromTypeWellName } from "components/screenshot/utils";

const ChartLegendItem = ({
  fontSize,
  legend,
  useCount,
  showTypeWellRescat = true,
  itemStyle,
  titleStyle,
  isScreenshotMode = false,
  ...rest
}) => {
  const dispatch = useDispatch();
  const scheduleHoverWrite = useHoverLegendWriter();

  const { id } = useChartsContext();
  const { chartType } = useChartEntities(id);

  const wrapperRef = useRef<HTMLDivElement>(null);

  // Store-level subscription — toggles 'highlighted' CSS class directly on the DOM
  // element without triggering React re-renders. This eliminates N×M re-renders
  // (N legend items × M charts) per hover state change.
  const transformTitle = useCallback((t: string) => removeProbitInfoFromtitle(t), []);
  useHoverHighlightClass(wrapperRef, legend.title, legend.id, transformTitle);

  // events
  //
  // `legend.disabled` marks an item that labels something the map cannot
  // highlight — currently the Well Status All-Statuses legend, whose entries are
  // STATUSES while the hover write matches map bins by title. Left enabled it
  // would be a control that silently does nothing every time it is hovered.
  const handleMouseOver = () => {
    if (isScreenshotMode || legend.disabled) return;

    if (chartType.includes("Midstream")) {
      dispatch(setActiveFocus(EntityKind.Facility));
    } else {
      dispatch(setActiveFocus(EntityKind.Well));
    }

    scheduleHoverWrite({
      item: legend.title,
      id: legend.id,
      group: "",
      itemUnfiltered: "",
      isMultiProductChart: false
    });
  };

  const handleMouseOut = () => {
    if (isScreenshotMode || legend.disabled) return;
    scheduleHoverWrite(EMPTY_HOVER_LEGEND);
  };

  const getDisplayText = () => {
    // Type well has id to differentiate between same named type wells
    const isTypeWell = legend.id;
    const text = useCount ? legend.title : legend.value;
    // When showing counts, display the title; otherwise the value. Either way respect
    // the rescat setting.
    const displayed =
      showTypeWellRescat || !isTypeWell ? text : removeRescatFromTypeWellName(text);
    // Subscript the product token last, at render only: the stored legend.title is what
    // the map-hover highlight matches against, so it has to keep the backend spelling.
    return subscriptProductTokens(displayed);
  };

  return (
    <Wrapper
      ref={wrapperRef}
      data-testid="chart-legend-item-wrapper"
      $disabled={legend.disabled === true}
      className={isScreenshotMode ? "screenshot-mode" : ""}
      onPointerEnter={handleMouseOver}
      onPointerLeave={handleMouseOut}
      style={itemStyle}
      {...rest}>
      {legend.lineStyle != null ? (
        <LegendLineStyleContainer $height={fontSize + 4}>
          <LineStylePreview
            style={legend.lineStyle}
            color={legend.color}
            width={40}
            height={5}
          />
        </LegendLineStyleContainer>
      ) : (
        <Indicator
          data-testid="chart-legend-item-color-indicator"
          color={legend.color}
          fontSize={fontSize}
        />
      )}
      <Title className="legend-item-title" style={titleStyle}>
        {getDisplayText()}
      </Title>
    </Wrapper>
  );
};

export default ChartLegendItem;

const Wrapper = styled.div<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: transparent;
  backface-visibility: hidden;
  overflow: hidden;
  cursor: ${(props) => (props.$disabled ? "default" : "inherit")};

  // Suppressed for a disabled item. Gating only the handlers leaves it lighting
  // up under the cursor, which still reads as a control — the same "looks like it
  // does something, does nothing" failure, moved from behaviour to appearance.
  &.highlighted,
  &:hover {
    background-color: ${(props) => (props.$disabled ? "transparent" : "#dbdbdb")};
  }

  // Don't display legend item backgrounds on hover & active in screenshot mode
  &.screenshot-mode.highlighted,
  &.screenshot-mode:hover {
    background-color: transparent;
  }
`;

const Indicator = styled.div`
  flex-shrink: 0;
  width: ${(props) => props.fontSize + 4 + "px" || "18px"};
  height: ${(props) => props.fontSize + 4 + "px" || "18px"};
  border-radius: 2px;
  background-color: ${(props) => props.color};
`;

const Title = styled.div`
  user-select: none;
`;

const LegendLineStyleContainer = styled.div<{ $height: number }>`
  height: ${(props) => props.$height}px;
  display: flex;
  align-items: center;
`;
